import React, { useEffect, useRef, useState } from 'react';
import { useSmallVideo } from '@/components/GridScenarioVideos';

// Abschnitt mit Erklärvideo im Hintergrund (helle Seite).
//  1. Erreicht der Abschnitt die Bildschirmmitte, startet das Video von vorn.
//  2. Die ersten `textSeconds` Sekunden stehen die Inhalte (children) gut lesbar vor dem dezenten Video.
//  3. Danach werden sie nach oben klein weggewischt, das Video wird heller und kontrastreicher
//     und rückt in den Vordergrund.
//  4. Beim Neustart des Videos (Loop) kommen die Inhalte wieder. Ein Button holt sie jederzeit zurück.
//  5. Verlässt der Abschnitt die Mitte: Video pausiert, Inhalte stehen wieder da.
// Handy / Datensparmodus: kleine Videodatei. "Bewegung reduzieren": kein Video, Inhalte bleiben stehen.
export default function VideoExplainerSection({
  children,
  testId,
  srcBig = '/videos/invest-background.mp4',
  srcSmall = '/videos/invest-background-small.mp4',
  textSeconds = 20,
  showAgainLabel = 'Texte einblenden',
}) {
  const small = useSmallVideo();
  const src = small ? srcSmall : srcBig;
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const showUntil = useRef(0);
  const [active, setActive] = useState(false);
  const [textVisible, setTextVisible] = useState(true);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  // Start/Stopp, sobald der Abschnitt die Bildschirmmitte erreicht
  useEffect(() => {
    const section = sectionRef.current;
    const video = videoRef.current;
    if (reduced || !section || !video || typeof IntersectionObserver === 'undefined') return undefined;
    let on = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !on) {
          on = true;
          showUntil.current = 0;
          video.currentTime = 0;
          setTextVisible(true);
          setActive(true);
          const p = video.play();
          if (p && p.catch) p.catch(() => {});
        } else if (!entry.isIntersecting && on) {
          on = false;
          video.pause();
          setActive(false);
          setTextVisible(true);
        }
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );
    io.observe(section);
    return () => io.disconnect();
  }, [src, reduced]);

  // Takt: Inhalte sind sichtbar, solange das Video in den ersten `textSeconds` Sekunden ist
  useEffect(() => {
    const video = videoRef.current;
    if (reduced || !video) return undefined;
    const onTime = () => {
      if (video.paused) return;
      const t = video.currentTime;
      setTextVisible(t < textSeconds || t < showUntil.current);
    };
    video.addEventListener('timeupdate', onTime);
    return () => video.removeEventListener('timeupdate', onTime);
  }, [src, reduced, textSeconds]);

  const showAgain = () => {
    const video = videoRef.current;
    if (!video) return;
    showUntil.current = video.currentTime + textSeconds;
    setTextVisible(true);
  };

  const focus = active && !textVisible;      // Video im Vordergrund
  const videoStyle = {
    filter: focus
      ? 'invert(1) hue-rotate(180deg) saturate(1.3) contrast(1.2) brightness(1.08)'
      : 'invert(1) hue-rotate(180deg) saturate(1.25)',
    mixBlendMode: 'multiply',
    opacity: !active ? 0 : focus ? 0.95 : 0.3,
    transition: 'opacity 900ms ease, filter 900ms ease',
  };
  const contentStyle = textVisible
    ? { opacity: 1, transform: 'none', transition: 'opacity 700ms ease, transform 700ms ease' }
    : {
        opacity: 0,
        transform: 'translateY(-90px) scale(0.55)',
        transformOrigin: '50% 0%',
        pointerEvents: 'none',
        visibility: 'hidden',
        transition: 'opacity 800ms ease, transform 900ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear 900ms',
      };

  return (
    <section
      ref={sectionRef}
      className="relative isolate overflow-hidden py-16 bg-white"
      // hoch genug, dass das 16:9-Video nicht unten abgeschnitten wird (aber nie höher als der Bildschirm)
      style={{ minHeight: reduced ? undefined : 'min(56.25vw, 92vh)' }}
      data-testid={testId}
    >
      {!reduced && (
        <div className="absolute inset-0 -z-10 pointer-events-none" aria-hidden="true">
          <video
            ref={videoRef}
            key={src}
            src={src}
            className="w-full h-full object-contain"
            style={videoStyle}
            muted
            loop
            playsInline
            preload="metadata"
            tabIndex={-1}
            data-testid="explainer-video"
          />
          {/* weicher Schleier hinter der Überschrift, solange die Inhalte stehen */}
          <div
            className="absolute inset-x-0 top-0 bg-gradient-to-b from-white via-white/70 to-transparent"
            style={{ height: '46%', opacity: textVisible ? 1 : 0, transition: 'opacity 900ms ease' }}
          />
        </div>
      )}

      <div className="relative z-10" style={reduced ? undefined : contentStyle} aria-hidden={!textVisible}>
        {children}
      </div>

      {focus && (
        <button
          type="button"
          onClick={showAgain}
          className="absolute bottom-4 right-4 z-20 rounded-full bg-slate-900/80 px-4 py-2 text-sm font-medium text-white backdrop-blur hover:bg-slate-900"
          data-testid="explainer-show-text"
        >
          {showAgainLabel}
        </button>
      )}
    </section>
  );
}
